// src/components/TopNav.jsx
import React from "react";
import { useLocation, matchPath, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { signOut } from "../api/client.js";

export default function TopNav({
  brandHref = "/",
  brandName = "",
  brandLogoSrc = `${process.env.PUBLIC_URL || ""}/dyne-logo-stacked.png`,
  brandLogoAlt = "Company logo",
  brandLogoClass = "h-14 md:h-16 w-auto object-contain",

  navItems = [
    {
      label: "Products",
      dropdown: [
        { label: "Buy-to-Let", to: "/buy-to-let-mortgage-calculator" },
        { label: "Invoice finance", to: "/invoice-finance-calculator-uk" },
        { label: "Complex BTL", to: "/mortgages/complex-buy-to-let-mortgage-calculator" },
        { label: "Semi-commercial", to: "/semi-commercial-calculator-uk", comingSoon: true },
        { label: "Commercial", to: "/commercial-calculator-uk", comingSoon: true },
        { label: "Bridging", to: "/bridging-calculator-uk", comingSoon: true },
        { label: "Development", to: "/development-calculator-uk", comingSoon: true },
        { label: "Asset finance", to: "/asset-finance-calculator-uk", comingSoon: true },
      ],
    },
    { to: "/borrowers", label: "For Borrowers" },
    { to: "/lenders", label: "For Lenders" },
    { to: "/blog", label: "Blogs/News" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact us" },
  ],
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [productsOpen, setProductsOpen] = React.useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const productsCloseTimerRef = React.useRef(null);

  React.useEffect(() => {
    const scrollTop = () => {
      const el = document.scrollingElement ?? document.documentElement;
      return window.scrollY ?? window.pageYOffset ?? el.scrollTop ?? 0;
    };
    const onScroll = () => setScrolled(scrollTop() > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("scroll", onScroll, { passive: true });
      vv.addEventListener("resize", onScroll, { passive: true });
    }
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (vv) {
        vv.removeEventListener("scroll", onScroll);
        vv.removeEventListener("resize", onScroll);
      }
    };
  }, []);

  React.useEffect(() => {
    setProductsOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    return () => {
      if (productsCloseTimerRef.current) {
        clearTimeout(productsCloseTimerRef.current);
      }
    };
  }, []);

  const openProductsMenu = () => {
    if (productsCloseTimerRef.current) {
      clearTimeout(productsCloseTimerRef.current);
      productsCloseTimerRef.current = null;
    }
    setProductsOpen(true);
  };

  const closeProductsMenuWithDelay = () => {
    if (productsCloseTimerRef.current) {
      clearTimeout(productsCloseTimerRef.current);
    }
    productsCloseTimerRef.current = setTimeout(() => {
      setProductsOpen(false);
      productsCloseTimerRef.current = null;
    }, 140);
  };

  const hasAuth =
    localStorage.getItem("ds_isAuthed") === "1" ||
    sessionStorage.getItem("ds_isAuthed") === "1";

  const borrowerPortal =
    localStorage.getItem("ds_portal") === "borrower" ||
    sessionStorage.getItem("ds_portal") === "borrower";

  const lenderPortal =
    localStorage.getItem("ds_portal") === "lender" ||
    sessionStorage.getItem("ds_portal") === "lender";

  const lenderEmail =
    localStorage.getItem("ds_lender_email") ||
    sessionStorage.getItem("ds_lender_email") ||
    "";

  const showLenderEmail =
    hasAuth &&
    lenderPortal &&
    Boolean(lenderEmail) &&
    Boolean(matchPath({ path: "/lender/*", end: false }, location.pathname));

  /* ------------------------------------------------------------
     Route-based button sets
  ------------------------------------------------------------ */
  const sourcingHeaderButtons =
    hasAuth && borrowerPortal
      ? [
        { label: "Dashboard", to: "/borrower/dashboard", variant: "outline-sky", minW: 200 },
        { label: "Sign out", to: "/borrower/signin", variant: "solid-sky", signOut: true },
      ]
      : [
        { label: "Register", to: "/borrower/signup", variant: "outline-sky", minW: 200 },
        { label: "Log in", to: "/borrower/signin", variant: "solid-sky" },
      ];

  const buttonsByRoute = [
    {
      pattern: "/mortgages/*",
      buttons: sourcingHeaderButtons,
    },
    {
      // Show Register / Log in on sourcing pages
      pattern: "/sourcing/*",
      buttons: sourcingHeaderButtons,
    },
  ];

  const defaultButtons = [
    {
      label: "Borrower",
      to: hasAuth && borrowerPortal ? "/borrower/dashboard" : "/borrower/signin",
      variant: "outline-sky",
      minW: 200
    },
    {
      label: hasAuth && lenderPortal ? "Lender Portal" : "Lender",
      to: hasAuth && lenderPortal ? "/lender/overview" : "/signin?force=true&next=/lender/overview",
      variant: "solid-sky"
    },
  ];

  const getButtonsForPath = (pathname) => {
    for (const rule of buttonsByRoute) {
      if (matchPath({ path: rule.pattern, end: false }, pathname)) {
        return rule.buttons;
      }
    }
    return defaultButtons;
  };

  const activeButtons = getButtonsForPath(location.pathname);

  /* ------------------------------------------------------------
     Styles
  ------------------------------------------------------------ */
  const headerBase = "pointer-events-none fixed top-4 left-0 w-full z-50";
  const barGlass =
    "bg-white/50 shadow border-slate-200/50";
  const barSolid =
    "bg-white shadow-md border-slate-200";
  const barBase =
    "pointer-events-auto transition-all duration-300 rounded-2xl border backdrop-blur " +
    (scrolled ? barGlass : barSolid);
  const mobileIconShell =
    "inline-flex h-9 w-9 items-center justify-center rounded-md border backdrop-blur text-slate-700 transition-all duration-300 " +
    (scrolled
      ? "border-slate-200/50 bg-white/45"
      : "border-slate-200/80 bg-white/80");
  const barWidth =
    "mx-auto w-[calc(100%-0.75rem)] sm:w-[calc(100%-1.5rem)] lg:w-[calc(100%-3rem)] max-w-7xl";
  const barInner =
    "h-16 px-3 sm:px-4 grid grid-cols-[auto,1fr,auto] items-center";

  const navWrap =
    "hidden md:flex items-center justify-center gap-5 whitespace-nowrap overflow-visible";
  const linkBase =
    "group relative px-2 py-1 text-sm font-medium rounded whitespace-nowrap " +
    "text-slate-700 hover:text-sky-700 transition-colors duration-150 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300";
  const dropdownItemClass =
    "block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-sky-50 hover:text-sky-700";
  const dropdownItemDisabledClass =
    "block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 cursor-default";

  const renderProductDropdownItem = (opt, { onNavigate, mobile = false } = {}) => {
    const badgeRow = (
      <div className="flex items-center justify-between gap-3">
        <span>{opt.label}</span>
        {opt.comingSoon ? (
          <span
            className={
              "rounded px-2 py-0.5 text-[11px] font-semibold text-slate-500 " +
              (mobile ? "bg-slate-200" : "bg-slate-100")
            }
          >
            Coming soon
          </span>
        ) : null}
      </div>
    );

    if (opt.comingSoon || !opt.to) {
      return (
        <div key={opt.label} className={dropdownItemDisabledClass} aria-disabled="true">
          {badgeRow}
        </div>
      );
    }

    return (
      <a
        key={opt.label}
        href={opt.to}
        className={mobile ? `${dropdownItemClass} text-[15px]` : dropdownItemClass}
        onClick={() => {
          onNavigate?.();
        }}
      >
        {badgeRow}
      </a>
    );
  };

  const baseBtn =
    "inline-flex items-center justify-center rounded-md font-semibold leading-tight tracking-wide whitespace-nowrap transition-colors text-[15px]";
  const btnVariants = {
    "outline-sky": (minW) =>
      `${baseBtn} px-5 py-2 border border-sky-300 text-sky-400 hover:bg-sky-50 ${minW ? `min-w-[${minW}px]` : ""
      }`,
    "solid-sky": () => `${baseBtn} px-5 py-2 bg-sky-400 text-white hover:bg-sky-500`,
  };

  // Use navigate() for guaranteed route switching. Sign out clears borrower portal auth then navigates.
  // When signing out from sourcing page, stay on sourcing (reload so header shows Register/Log in).
  const isSourcing =
    matchPath({ path: "/sourcing/*", end: false }, location.pathname) ||
    matchPath({ path: "/mortgages/*", end: false }, location.pathname);
  const renderButtons = (buttons, extra = "") =>
    buttons.map((b, i) => (
      <button
        key={`${b.label}-${i}`}
        onClick={async () => {
          if (b.signOut) {
            await signOut();
            if (isSourcing) {
              window.location.assign(location.pathname + location.search);
            } else {
              navigate(b.to);
            }
          } else {
            navigate(b.to);
          }
        }}
        className={`${btnVariants[b.variant]?.(b.minW) || baseBtn} ${extra}`}
      >
        {b.label}
      </button>
    ));

  /* ------------------------------------------------------------
     Render
  ------------------------------------------------------------ */
  return (
    <header className={headerBase} aria-label="Site navigation">
      <div className={`${barBase} ${barWidth}`}>
        <div className={barInner}>
          {/* LEFT: Logo */}
          <a
            href={brandHref}
            className="flex items-center gap-2 justify-start min-w-0"
            aria-label={brandName || "Home"}
          >
            {brandLogoSrc ? (
              <img src={brandLogoSrc} alt={brandLogoAlt} className={brandLogoClass} />
            ) : (
              <div className="h-6 w-6 rounded-md bg-sky-500 shadow-sm" />
            )}
            {brandName ? (
              <span className="text-base font-black tracking-tight leading-none">{brandName}</span>
            ) : null}
          </a>

          {/* CENTER: Nav */}
          <nav className={navWrap}>
            {navItems.map((item) => (
              item.dropdown ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={openProductsMenu}
                  onMouseLeave={closeProductsMenuWithDelay}
                >
                  <button
                    type="button"
                    className={`${linkBase} inline-flex items-center gap-1`}
                    onClick={() => {
                      setProductsOpen((v) => !v);
                    }}
                    aria-expanded={productsOpen}
                    aria-haspopup="menu"
                  >
                    <span className="relative inline-flex items-center gap-1.5">
                      <span className="relative">
                        {item.label}
                        <span
                          className={
                            "absolute left-0 right-0 -bottom-[2px] h-[2px] rounded bg-sky-400 " +
                            "origin-center transition-transform duration-200 ease-out " +
                            (productsOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100")
                          }
                        />
                      </span>
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
                  </button>

                  {productsOpen && (
                    <div className="absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-2">
                      <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                        {item.dropdown.map((opt) =>
                          renderProductDropdownItem(opt, {
                            onNavigate: () => setProductsOpen(false),
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <a key={item.to} href={item.to} className={linkBase}>
                  <span className="relative">
                    {item.label}
                    <span
                      className={
                        "absolute left-0 right-0 -bottom-[2px] h-[2px] rounded bg-sky-400 " +
                        "origin-center transition-transform duration-200 ease-out scale-x-0 group-hover:scale-x-100"
                      }
                    />
                  </span>
                </a>
              )
            ))}
          </nav>

          {/* RIGHT: Route-based buttons */}
          <div className="hidden md:flex items-center gap-2">
            {showLenderEmail ? (
              <div className="mr-2 max-w-[260px] truncate rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                {lenderEmail}
              </div>
            ) : null}
            {renderButtons(activeButtons)}
          </div>

          {/* MOBILE: Toggle */}
          <div className="md:hidden justify-self-end">
            <button
              className={mobileIconShell}
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE DROPDOWN */}
      {open && (
        <div
          className={
            "pointer-events-auto mx-auto mt-2 w-[calc(100%-0.75rem)] sm:w-[calc(100%-1.5rem)] lg:w-[calc(100%-3rem)] max-w-7xl rounded-2xl border backdrop-blur shadow-xl ring-1 ring-black/5 md:hidden " +
            (scrolled
              ? "border-slate-200/50 bg-white/55"
              : "border-slate-200/80 bg-white/95")
          }
        >
          <div className="px-4 py-4 flex flex-col items-center text-center gap-2">
            {showLenderEmail ? (
              <div className="mb-1 w-full truncate rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                {lenderEmail}
              </div>
            ) : null}
            {renderButtons(activeButtons, "w-full")}
            <div className="my-2 h-px w-full bg-slate-200" />
            {navItems.map((item) => (
              item.dropdown ? (
                <div key={item.label} className="w-full rounded-xl border border-slate-200 bg-slate-50 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileProductsOpen((v) => !v);
                    }}
                    className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    aria-expanded={mobileProductsOpen}
                    aria-controls="mobile-products-menu"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {item.label}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileProductsOpen && (
                    <div id="mobile-products-menu" className="mt-1 max-h-64 overflow-y-auto pr-1">
                      {item.dropdown.map((opt) =>
                        renderProductDropdownItem(opt, {
                          onNavigate: () => setOpen(false),
                          mobile: true,
                        })
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.to}
                  href={item.to}
                  className="py-3 text-[15px] font-medium text-slate-700 hover:text-sky-700 w-full"
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </a>
              )
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
